import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, Trash2, ArrowLeft, ShoppingCart, Minus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Home Pantry / Inventory — part of My Social Pulse. Household-shared stock
   with locations, quantities, expiry warnings and one-tap restock. */

const LOCS = [["pantry", "Pantry"], ["fridge", "Fridge"], ["freezer", "Freezer"], ["garage", "Garage"], ["bathroom", "Bathroom"], ["other", "Other"]];

const daysTo = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

export default function PantryPage() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", qty: "1", unit: "", location: "pantry", expiry_date: "" });
  const load = useCallback(() => api.get("/life/pantry").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    try { await api.post("/life/pantry", form); setForm((f) => ({ ...f, name: "", qty: "1", expiry_date: "" })); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add item"); }
  };
  const bump = (it, d) => api.put(`/life/pantry/${it.id}`, { qty: Math.max(0, (it.qty || 0) + d) }).then(load).catch(() => toast.error("Update failed"));
  const del = (it) => api.delete(`/life/pantry/${it.id}`).then(load).catch(() => toast.error("Delete failed"));
  const restock = (it) => api.post(`/life/pantry/${it.id}/to-shopping`).then(() => toast.success(`"${it.name}" added to the shopping list`)).catch(() => toast.error("Could not add"));

  const expiring = (items || []).filter((i) => { const d = daysTo(i.expiry_date); return d !== null && d <= 7; });

  return (
    <div className="max-w-4xl mx-auto px-5 py-8" data-testid="pantry-page">
      <Link to="/my-social-pulse" className="text-sm text-blue-700 font-600 inline-flex items-center gap-1 hover:underline" data-testid="pantry-back"><ArrowLeft className="w-4 h-4" /> My Social Pulse</Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight mt-2 flex items-center gap-3"><Package className="w-8 h-8 text-amber-600" /> Home Pantry</h1>
      <p className="text-zinc-600 mt-1">Everything in stock — food and beyond. Shared with your household.</p>

      {expiring.length > 0 && (
        <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm" data-testid="pantry-expiring">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <span><b>Use soon:</b> {expiring.map((i) => `${i.name}${daysTo(i.expiry_date) < 0 ? " (expired)" : ` (${daysTo(i.expiry_date)}d)`}`).join(", ")}</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2 bg-white border border-zinc-200 rounded-2xl p-3">
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add an item — rice, detergent, coffee…" data-testid="pantry-name-input"
          className="flex-1 min-w-[170px] border border-zinc-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:border-amber-600" />
        <input value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} placeholder="Qty" type="number" min="0" data-testid="pantry-qty-input"
          className="w-20 border border-zinc-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:border-amber-600" />
        <input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="Unit" data-testid="pantry-unit-input"
          className="w-20 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-600" />
        <select value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="pantry-loc-select"
          className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm bg-white">
          {LOCS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} title="Expiry date (optional)" data-testid="pantry-expiry-input"
          className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm" />
        <button onClick={add} data-testid="pantry-add-btn"
          className="inline-flex items-center gap-1.5 bg-amber-600 text-white px-5 py-2.5 rounded-lg font-700 hover:bg-amber-500"><Plus className="w-4 h-4" /> Add</button>
      </div>

      {!items ? <p className="mt-5 text-sm text-zinc-500">Loading…</p> : items.length === 0 ? (
        <p className="mt-6 text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-xl">Your pantry is empty — add what you have at home.</p>
      ) : (
        <div className="mt-6 space-y-5">
          {LOCS.map(([v, l]) => {
            const rows = items.filter((i) => i.location === v);
            if (rows.length === 0) return null;
            return (
              <div key={v} data-testid={`pantry-group-${v}`}>
                <div className="text-xs font-mono uppercase text-amber-700 font-700 mb-1.5">{l} ({rows.length})</div>
                <ul className="space-y-1.5">
                  {rows.map((it) => {
                    const d = daysTo(it.expiry_date);
                    return (
                      <li key={it.id} className="flex flex-wrap items-center gap-2.5 bg-white border border-zinc-200 rounded-xl px-4 py-2.5" data-testid={`pantry-item-${it.id}`}>
                        <span className="text-base font-600">{it.name}</span>
                        {d !== null && d <= 7 && (
                          <span className={`text-[10px] font-800 uppercase px-1.5 py-0.5 rounded border ${d < 0 ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-amber-50 border-amber-300 text-amber-700"}`}>
                            {d < 0 ? "expired" : `${d}d left`}
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-1">
                          <button onClick={() => bump(it, -1)} data-testid={`pantry-minus-${it.id}`} className="p-1.5 border border-zinc-300 rounded hover:border-amber-600"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="w-14 text-center font-mono text-sm" data-testid={`pantry-qty-${it.id}`}>{it.qty}{it.unit ? ` ${it.unit}` : ""}</span>
                          <button onClick={() => bump(it, 1)} data-testid={`pantry-plus-${it.id}`} className="p-1.5 border border-zinc-300 rounded hover:border-amber-600"><Plus className="w-3.5 h-3.5" /></button>
                        </span>
                        <button onClick={() => restock(it)} title="Add to shopping list" data-testid={`pantry-restock-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100"><ShoppingCart className="w-3.5 h-3.5" /> Restock</button>
                        <button onClick={() => del(it)} data-testid={`pantry-del-${it.id}`} className="text-zinc-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
